import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import classNames from "classnames";
import SVGIcon from "components/v2/Images/SVGIcon";
import { NOTIFICATION_STATUS, NOTIFICATION_TITLE, NOTIFICATION_TYPES } from "constants/v2/notifications";
import { SOCIAL_MEDIA_PUBLISH } from "constants/v2/publishConstants";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import {
  selectNotificationAspectRatioById,
  selectNotificationClipNameById,
  selectNotificationCreatedAtById,
  selectNotificationDetailsById,
  selectNotificationExportTypeById,
  selectNotificationImageById,
  selectNotificationPageUrlById,
  selectNotificationProgressById,
  selectNotificationStatusById,
  selectNotificationStreamNameById,
  selectNotificationTypeById,
} from "store/core/notifications/selectors";

import styles from "./style.module.css";

const SeparatorIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="3" height="2" viewBox="0 0 3 2" fill="none">
      <circle opacity="0.4" cx="1.9375" cy="1" r="1" fill="white" />
    </svg>
  );
};

const NotificationCard = ({
  type,
  image,
  progress,
  createdAt,
  status,
  platforms = [SOCIAL_MEDIA_PUBLISH.FACEBOOK],
  streamName,
  clipName,
  pageUrl,
  isToast = false,
  exportType,
  aspectRatio,
}) => {
  const navigate = useNavigate();

  const headerRightContent = useMemo(() => {
    switch (type) {
      case NOTIFICATION_TYPES.VARIANT:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/variant.svg" />;
      case NOTIFICATION_TYPES.PUBLISH:
        return (
          <div className={styles.publishContainer}>
            {platforms?.map((platform) => (
              <div
                className={classNames(styles.iconContainer, {
                  [styles.facebookIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.FACEBOOK,
                  [styles.instagramIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.INSTAGRAM,
                  [styles.linkedinIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.LINKEDIN,
                  [styles.youtubeIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.YOUTUBE,
                  [styles.twitterIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.TWITTER,
                  [styles.telegramIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.TELEGRAM,
                  [styles.tiktokIconContainer]: platform === SOCIAL_MEDIA_PUBLISH.TIKTOK,
                })}
              >
                <SVGIcon src={`components/v2/PublishHistory/${platform}.svg`} className={styles.publishIcon} />
              </div>
            ))}
          </div>
        );
      case NOTIFICATION_TYPES.EXPORT:
        return <span className={styles.rightContent}>{exportType}</span>;
      case NOTIFICATION_TYPES.COPILOT:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/copilot.svg" />;
      case NOTIFICATION_TYPES.HIGHLIGHT:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/highlight.svg" />;
      case NOTIFICATION_TYPES.RULE_ENGINE:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/rule-engine.svg" />;
      case NOTIFICATION_TYPES.AUTOFLIP:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/autoFlip.svg" />;
      case NOTIFICATION_TYPES.DYNAMICFLIP:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/dynamicFlip.svg" />;
      case NOTIFICATION_TYPES.DOWNLOAD_VIDEO:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/downloadVideo.svg" />;
      case NOTIFICATION_TYPES.SUBTITLE_GENERATION:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/subtitleGeneration.svg" />;
      case NOTIFICATION_TYPES.CENTER_CROP:
        return <SVGIcon className={styles.icon} src="components/v2/Sidebar/Notifications/autoFlip.svg" />;
      default:
        return null;
    }
  }, [platforms, type, exportType]);

  const elapsedTime = useMemo(() => {
    return moment(createdAt).fromNow();
  }, [createdAt]);

  const title = useMemo(() => {
    return NOTIFICATION_TITLE?.[type]?.[status];
  }, [type, status]);

  const handleViewClick = useCallback(() => {
    navigate(pageUrl);
  }, [navigate, pageUrl]);

  const label = useMemo(() => {
    return [clipName, aspectRatio, streamName].filter(Boolean);
  }, [clipName, aspectRatio, streamName]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {image ? (
            <div className={styles.imageContainer}>
              <img src={image} alt="notification" className={styles.image} />
            </div>
          ) : null}
          <div className={styles.headerTitle}>
            <div className={styles.title}>{title}</div>
            <div className={classNames(styles.label, { [styles.toastLabel]: isToast })}>
              {label.map((item, index) => (
                <React.Fragment key={index}>
                  {item}
                  {index < label.length - 1 && <SeparatorIcon />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className={classNames({ [styles.toastHeaderRight]: isToast }, styles.headerRight)}>
          {headerRightContent}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.statusContainer}>
          {pageUrl ? (
            <div className={classNames(styles.statusBtn, styles.completed)} onClick={handleViewClick}>
              View
            </div>
          ) : null}
          {status === NOTIFICATION_STATUS.IN_PROGRESS ? (
            <div className={styles.progress}>
              <Spin indicator={<LoadingOutlined spin className={styles.loadingIcon} />} size="small" /> {progress}% done
            </div>
          ) : null}
          {/* {status === NOTIFICATION_STATUS.FAILED ? (
            <div className={classNames(styles.statusBtn, styles.failed)}>Retry</div>
          ) : null} */}
        </div>

        <div className={styles.elapsedTime}>{elapsedTime}</div>
      </div>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  notification: selectNotificationDetailsById,
  type: selectNotificationTypeById,
  image: selectNotificationImageById,
  progress: selectNotificationProgressById,
  createdAt: selectNotificationCreatedAtById,
  clipName: selectNotificationClipNameById,
  streamName: selectNotificationStreamNameById,
  status: selectNotificationStatusById,
  pageUrl: selectNotificationPageUrlById,
  exportType: selectNotificationExportTypeById,
  aspectRatio: selectNotificationAspectRatioById,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationCard);
